import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { userAction } from "@/core/telemetry/telemetry";
import type { Business } from "@/features/business/types/businessTypes";
import { FormDefinitionView } from "@/features/form-builder/components/FormDefinitionView";
import { Button } from "@/shared/components/shadcn/Button";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent, PageFooter } from "@/shared/components/ui/Page";
import { PageHeader, PageHeaderAction } from "@/shared/components/ui/PageHeader";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useOperationLifetime } from "@/shared/hooks/useOperationLifetime";
import { FormBusinessBoundary } from "../components/FormBusinessBoundary";
import { FormMetadata } from "../components/FormMetadata";
import { FormPageState } from "../components/FormPageState";
import { FormUnavailable } from "../components/FormUnavailable";
import { useDeleteForm, useForm } from "../hooks/formQueries";

function FormDetails({ business, formId }: { business: Business; formId: string }) {
  const query = useForm(business.id, formId);
  const mutation = useDeleteForm(business.id, formId);
  const { goBack, goToHome, goToFormEdit } = useAppNavigation();
  const captureOperation = useOperationLifetime();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);

  async function deleteForm() {
    if (!confirming || submitting.current) return;
    submitting.current = true;
    const isCurrent = captureOperation();
    setError("");
    try {
      await mutation.mutateAsync();
      if (isCurrent()) goToHome();
    } catch (cause) {
      if (isCurrent()) setError(cause instanceof Error ? cause.message : "Unable to delete this form.");
    } finally {
      submitting.current = false;
    }
  }

  if (query.isLoading) return <FormPageState title="Form details"><LoadingSpinner message="Loading form..." /></FormPageState>;
  if (query.error || !query.data) return <FormPageState title="Form details"><FormUnavailable error={query.error} onRetry={() => void query.refetch()} /></FormPageState>;
  const form = query.data;
  return (
    <Page>
      <PageHeader title={form.name} description={<FormMetadata form={form} />}
        actions={<PageHeaderAction onClick={goBack}>Back</PageHeaderAction>} />
      <PageContent className="space-y-[26px]">
        <FormDefinitionView definition={form.definition} />
      </PageContent>
      <PageFooter>
        <Button variant="outline" disabled={mutation.isPending} onClick={() => setConfirming(true)}>Delete form</Button>
        <Button disabled={mutation.isPending} onClick={() => goToFormEdit(business.id, form.id)} {...userAction("form.edit_start")}>Edit form</Button>
      </PageFooter>
      <ConfirmationDialog open={confirming} onOpenChange={setConfirming} title="Delete this form?"
        description={`“${form.name}” will no longer be available to anyone in ${business.name}. There is no restore option.`}
        confirmLabel="Delete form" dismissLabel="Keep form" destructive pending={mutation.isPending} error={error}
        onConfirm={() => void deleteForm()} />
    </Page>
  );
}

export function FormDetailsPage() {
  const { formId = "" } = useParams<{ formId: string }>();
  return <FormBusinessBoundary title="Form details">{business => <FormDetails key={`${business.id}:${formId}`} business={business} formId={formId} />}</FormBusinessBoundary>;
}
